import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import openCv, { openLink } from './openCv';

const API = 'https://carriera-backend.onrender.com/api';

// "CV & call notes" (candidate inventory Phase 2c, 2 Nov 2026). Steph's paper
// phone-screen sheet, in the dashboard: the CV on the left, the call-notes form on
// the right, and "Ready for profile", which leaves Kate an Ask Kate request.
// - The PDF viewer is Mozilla's ready-made PDF.js viewer (public/pdfjs, v6.4.299),
//   with its own pen / highlight / text box toolbar. It sits in a frame on the same
//   address as the dashboard, so this page hands it the CV and asks it for the
//   marked-up file (saveDocument). The marked-up copy is saved separately
//   (POST /candidates/:id/cv-annotated); the original CV is never changed.
// - The form is saved with PUT /candidates/:id/call-notes. Its fields are checked by
//   the backend's utils/callNotes.js and each one maps onto a rule in
//   PROFILE_WRITING_RULES.md (see the hints under the fields).
// Both saves are refused for Kate's login (role "agent"); she sees it read-only.

const VIEWER = `${process.env.PUBLIC_URL}/pdfjs/web/viewer.html?file=`;
const INPUT = 'w-full border border-carriera-border px-2 py-1.5 text-sm focus:outline-none focus:border-carriera-accent disabled:bg-carriera-bg';
const READY_TEXT = 'Ready for profile: please prepare the candidate profile from the CV, my call notes and the marked-up CV.';

const BLANK_JOB = {
  employer: '', position: '', from: '', to: '', reports_to: '', subordinates: '', headcount: '',
  last_drawn: '', reason_for_leaving: '', industry_note: '', able_to: [], drop: ''
};
const BLANK = {
  gender: '', marital_status: '', children: '', own_transport: '', date_of_birth: '', location: '', willing_to_relocate: false,
  english: '', mandarin_confirmed: false, expected_salary: '', availability: '', notice_months: '', available_from: '',
  current_basic: '', allowances: [], jobs: [], notes: ''
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function jobLabel(job) {
  if (!job) return 'No specific job';
  return job.clients?.company_name ? `${job.title} · ${job.clients.company_name}` : job.title;
}

// Stored notes use null for blanks; the form uses '' so inputs stay controlled.
function toForm(notes, c) {
  if (!notes) {
    return {
      ...BLANK,
      location: c.location || '',
      expected_salary: c.expected_salary ?? '',
      jobs: c.current_company ? [{ ...BLANK_JOB, employer: c.current_company, position: c.current_job_title || '' }] : []
    };
  }
  const blankless = (obj, base) => Object.fromEntries(Object.keys(base).map(k => [k, obj[k] ?? base[k]]));
  return {
    ...blankless(notes, BLANK),
    allowances: (notes.allowances || []).map(a => ({ label: a.label, amount: a.amount })),
    jobs: (notes.jobs || []).map(j => ({ ...blankless(j, BLANK_JOB), able_to: j.able_to || [] }))
  };
}

// Opens a 5-minute profile-draft link.
export function openProfile(screeningId) {
  return openLink(`/screenings/${screeningId}/profile-link`, authHeaders(), 'Could not open the profile.');
}

// A pen stroke or text box still being drawn is not in the PDF yet. PDF.js's own
// Save button finishes it first (downloadOrSave in web/viewer.mjs); we do the same.
// _layerProperties is internal to the viewer: recheck after a PDF.js upgrade.
function finishEditing(app) {
  app.pdfViewer._layerProperties?.annotationEditorUIManager?.endCurrentEditing();
}

function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-carriera-muted">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-carriera-muted mt-0.5">{hint}</span>}
    </label>
  );
}

function Heading({ children }) {
  return <p className="text-xs font-semibold text-carriera-muted uppercase tracking-widest mt-5 mb-2">{children}</p>;
}

// ── The CV (left side) ────────────────────────────
function CvViewer({ candidate, canEdit, viewerRef, onMarksSaved }) {
  const frame = useRef(null);
  const [status, setStatus] = useState('Loading the CV...');
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const ext = (candidate.cv_path || '').split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const showing = candidate.cv_annotated_path ? 'annotated' : 'original';

  async function cvUrl(which) {
    const q = which === 'annotated' ? '?which=annotated' : '';
    const { data } = await axios.get(`${API}/candidates/${candidate.id}/cv-link${q}`, { headers: authHeaders() });
    return data.url;
  }

  useEffect(() => {
    if (!candidate.cv_path || isPdf) return;
    if (['jpg', 'jpeg', 'png'].includes(ext)) {
      cvUrl('original').then(setImageUrl).catch(() => setStatus('Could not load the CV.'));
    }
  }, [candidate.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Runs when the PDF.js frame has loaded: wait for the viewer, then give it the file.
  async function loadPdf() {
    try {
      const win = frame.current.contentWindow;
      // The viewer's own "Open file" would load a different PDF into this candidate's panel.
      // (Hidden by attribute: the viewer's security policy blocks added style tags.)
      win.document.getElementById('secondaryOpenFile')?.setAttribute('hidden', '');
      let app = win.PDFViewerApplication;
      for (let i = 0; !app && i < 50; i++) {
        await new Promise(r => setTimeout(r, 100));
        app = win.PDFViewerApplication;
      }
      if (!app) throw new Error('viewer did not start');
      await app.initializedPromise;
      const res = await fetch(await cvUrl(showing));
      if (!res.ok) throw new Error(`download ${res.status}`);
      await app.open({ data: new Uint8Array(await res.arrayBuffer()) });
      viewerRef.current = { app, savedCount: 0 };
      setStatus('');
    } catch (err) {
      console.error('CV viewer:', err);
      setStatus('Could not open the CV in the viewer. Use "Open original" instead.');
    }
  }

  async function saveMarks() {
    const v = viewerRef.current;
    if (!v?.app.pdfDocument) return;
    setSaving(true);
    try {
      finishEditing(v.app);
      const bytes = await v.app.pdfDocument.saveDocument();
      const form = new FormData();
      form.append('cv', new Blob([bytes], { type: 'application/pdf' }), 'annotated.pdf');
      const { data } = await axios.post(`${API}/candidates/${candidate.id}/cv-annotated`, form, { headers: authHeaders() });
      v.savedCount = v.app.pdfDocument.annotationStorage.size;
      onMarksSaved(data.cv_annotated_path);
      setStatus('Marks saved.');
    } catch (err) {
      setStatus(err.response?.data?.error || 'Could not save the marks.');
    } finally {
      setSaving(false);
    }
  }

  if (!candidate.cv_path) {
    return <div className="flex-1 flex items-center justify-center text-sm text-carriera-muted">No CV stored for this candidate.</div>;
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 border-r border-carriera-border">
      <div className="flex items-center gap-3 px-4 py-2 border-b border-carriera-border bg-carriera-bg text-xs">
        <span className="text-carriera-muted">
          {isPdf ? (showing === 'annotated' ? 'Showing the marked-up copy' : 'Showing the original CV') : 'Picture CV: no pen on pictures'}
        </span>
        <button onClick={() => openCv(candidate.id, authHeaders())} className="text-carriera-accent hover:underline">Open original</button>
        {status && <span className="text-carriera-muted">{status}</span>}
        {isPdf && canEdit && (
          <button onClick={saveMarks} disabled={saving || !!status.startsWith('Loading')}
            className="ml-auto bg-carriera-text text-white px-3 py-1.5 hover:bg-carriera-accent transition-colors disabled:opacity-40">
            {saving ? 'Saving...' : 'Save my marks'}
          </button>
        )}
      </div>
      {isPdf ? (
        <iframe ref={frame} src={VIEWER} onLoad={loadPdf} title="CV" className="flex-1 w-full border-0" />
      ) : imageUrl ? (
        <div className="flex-1 overflow-auto p-4 bg-carriera-bg"><img src={imageUrl} alt="CV" className="max-w-full mx-auto shadow" /></div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-sm text-carriera-muted p-6 text-center">
          This CV is a Word file and cannot be shown here. Use "Open original".
        </div>
      )}
    </div>
  );
}

// ── One past or current job ───────────────────────
function JobNotes({ job, index, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...job, [k]: v });
  const current = index === 0;
  return (
    <div className={`border px-3 py-3 mb-3 ${job.drop ? 'border-red-200 bg-red-50/40' : 'border-carriera-border'}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-carriera-text">{current ? 'Current / last job' : `Job ${index + 1}`}</p>
        <button type="button" onClick={onRemove} className="text-xs text-carriera-muted hover:text-red-500">Remove</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Employer" className="col-span-2">
          <input className={INPUT} value={job.employer} onChange={e => set('employer', e.target.value)} />
        </Field>
        <Field label="Position (only if the CV is wrong)">
          <input className={INPUT} value={job.position} onChange={e => set('position', e.target.value)} />
        </Field>
        <Field label="Industry (e.g. manufacture textiles)">
          <input className={INPUT} value={job.industry_note} onChange={e => set('industry_note', e.target.value)} />
        </Field>
        <Field label="From (only if the CV is wrong)">
          <input className={INPUT} value={job.from} onChange={e => set('from', e.target.value)} placeholder="e.g. Mar 2020" />
        </Field>
        <Field label="To">
          <input className={INPUT} value={job.to} onChange={e => set('to', e.target.value)} placeholder="e.g. Dec 2022" />
        </Field>
        <Field label="R: reports to">
          <input className={INPUT} value={job.reports_to} onChange={e => set('reports_to', e.target.value)} />
        </Field>
        <Field label="U: subordinates" hint='Blank = "Nil."'>
          <input className={INPUT} value={job.subordinates} onChange={e => set('subordinates', e.target.value)} />
        </Field>
        <Field label="Headcount" hint="Blank = no headcount line">
          <input className={INPUT} value={job.headcount} onChange={e => set('headcount', e.target.value)} placeholder="e.g. 200 plus" />
        </Field>
        {!current && (
          <Field label="$: last drawn salary (RM)" hint="Blank = row left out">
            <input className={INPUT} type="number" min="0" step="0.01" value={job.last_drawn} onChange={e => set('last_drawn', e.target.value)} />
          </Field>
        )}
        <Field label={current ? 'Why looking out' : 'Reason for leaving'} hint="Kate uses your words" className="col-span-2">
          <input className={INPUT} value={job.reason_for_leaving} onChange={e => set('reason_for_leaving', e.target.value)} />
        </Field>
        <Field label='"Able to" notes, one per line' hint='Each becomes "Able to ...". Not a confirmed duty.' className="col-span-2">
          <textarea className={INPUT} rows={2} value={job.able_to.join('\n')}
            onChange={e => set('able_to', e.target.value.split('\n'))} placeholder="e.g. handle full set accounts" />
        </Field>
        <Field label="Drop this job from the profile" className="col-span-2">
          <select className={INPUT} value={job.drop} onChange={e => set('drop', e.target.value)}>
            <option value="">Keep</option>
            <option value="crossed_out">Drop: crossed out</option>
            <option value="part_time">Drop: part-time</option>
          </select>
        </Field>
      </div>
    </div>
  );
}

// ── The panel ─────────────────────────────────────
export default function CvNotes({ candidateId, screeningId, onClose }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canEdit = user.role !== 'agent';
  const viewerRef = useRef(null);
  const [candidate, setCandidate] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [screenings, setScreenings] = useState([]);
  const [pick, setPick] = useState(screeningId || '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState('');   // screening Kate was just asked about: no second request
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/candidates/${candidateId}`, { headers: authHeaders() }),
      axios.get(`${API}/screenings?candidate_id=${candidateId}`, { headers: authHeaders() })
    ]).then(([c, s]) => {
      setCandidate(c.data);
      setForm(toForm(c.data.call_notes, c.data));
      const withJob = s.data.filter(r => r.job_id);
      setScreenings(withJob);
      if (!screeningId && withJob.length === 1) setPick(withJob[0].id);
    }).catch(err => setLoadError(err.response?.data?.error || 'Could not load this candidate.'));
  }, [candidateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setJob = (i, job) => setForm(f => ({ ...f, jobs: f.jobs.map((j, n) => n === i ? job : j) }));

  function unsavedMarks() {
    const v = viewerRef.current;
    if (!v?.app.pdfDocument) return false;
    finishEditing(v.app);
    return v.app.pdfDocument.annotationStorage.size > v.savedCount;
  }

  function close() {
    if (canEdit && unsavedMarks() && !window.confirm('Your marks on the CV are not saved. Close anyway?')) return;
    onClose();
  }

  async function saveNotes() {
    const body = {
      ...form,
      allowances: form.allowances.filter(a => a.label || a.amount !== ''),
      jobs: form.jobs.map(j => ({ ...j, able_to: j.able_to.map(a => a.trim()).filter(Boolean) }))
    };
    const { data } = await axios.put(`${API}/candidates/${candidateId}/call-notes`, body, { headers: authHeaders() });
    setCandidate(c => ({ ...c, ...data }));
  }

  async function onSave() {
    setBusy(true);
    setMessage('');
    try {
      await saveNotes();
      setMessage('Call notes saved.');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not save the call notes.');
    } finally {
      setBusy(false);
    }
  }

  async function onReady() {
    const s = screenings.find(r => r.id === pick);
    if (!s) { setMessage('Pick the job the profile is for.'); return; }
    if (unsavedMarks() && !window.confirm('Your marks on the CV are not saved, so Kate will not see them. Continue?')) return;
    setBusy(true);
    setMessage('');
    try {
      await saveNotes();
      await axios.post(`${API}/kate-requests`, {
        about_type: 'screening', about_id: s.id, about_label: `${candidate.name} · ${jobLabel(s.jobs)}`, request: READY_TEXT
      }, { headers: authHeaders() });
      setAsked(s.id);
      setMessage('Notes saved and Kate asked for the profile. Her reply shows under Ask Kate (usually within 30 minutes on weekdays).');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not ask Kate.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-carriera-border">
        <div>
          <p className="font-serif text-lg text-carriera-text">{candidate?.name || 'Loading...'}</p>
          <p className="text-xs text-carriera-muted">
            CV & call notes
            {candidate?.call_notes_updated_at && `. Notes last saved by ${candidate.call_notes_updated_by || 'someone'}, ${new Date(candidate.call_notes_updated_at).toLocaleString('en-MY')}`}
          </p>
        </div>
        <button onClick={close} className="text-sm border border-carriera-border px-4 py-1.5 hover:border-carriera-accent">Close</button>
      </div>

      {loadError && <p className="p-6 text-sm text-red-600">{loadError}</p>}
      {candidate && (
        <div className="flex-1 flex min-h-0">
          <CvViewer candidate={candidate} canEdit={canEdit} viewerRef={viewerRef}
            onMarksSaved={path => setCandidate(c => ({ ...c, cv_annotated_path: path }))} />

          <div className="w-[440px] flex-shrink-0 overflow-y-auto px-5 pb-6">
            {!canEdit && <p className="text-xs text-carriera-muted mt-4">Only Ted or Steph fill in call notes.</p>}
            <fieldset disabled={!canEdit}>
              <Heading>About the candidate</Heading>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Gender (Mr. / Ms.)" hint="Only if confirmed; never guessed" className="col-span-2">
                  <select className={INPUT} value={form.gender} onChange={e => set('gender', e.target.value)}>
                    <option value="">Not confirmed</option><option value="male">Male (Mr.)</option><option value="female">Female (Ms.)</option>
                  </select>
                </Field>
                <Field label="Status: marital">
                  <select className={INPUT} value={form.marital_status} onChange={e => set('marital_status', e.target.value)}>
                    <option value="">Not asked</option><option value="single">Single</option><option value="married">Married</option>
                    <option value="divorced">Divorced</option><option value="widowed">Widowed</option>
                  </select>
                </Field>
                <Field label="Children">
                  <input className={INPUT} type="number" min="0" value={form.children} onChange={e => set('children', e.target.value)} />
                </Field>
                <Field label="Trans: own transport">
                  <select className={INPUT} value={form.own_transport} onChange={e => set('own_transport', e.target.value)}>
                    <option value="">Not asked</option><option value="yes">Yes</option><option value="no">No</option>
                  </select>
                </Field>
                <Field label="Bdate: date of birth">
                  <input className={INPUT} value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} placeholder="e.g. 16 Nov 1996" />
                </Field>
                <Field label="Loc: where they live now" className="col-span-2" hint='"Area, City" or "City, State"'>
                  <input className={INPUT} value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Seksyen 25, Shah Alam" />
                </Field>
                <label className="col-span-2 flex items-center gap-2 text-xs text-carriera-sec">
                  <input type="checkbox" checked={form.willing_to_relocate} onChange={e => set('willing_to_relocate', e.target.checked)} />
                  Willing to relocate
                </label>
                <Field label="English (tested on the call)" hint="Not tested = English left out of the profile">
                  <select className={INPUT} value={form.english} onChange={e => set('english', e.target.value)}>
                    <option value="">-</option><option value="excellent">Excellent</option><option value="good">Good</option>
                    <option value="fair">Fair</option><option value="basic">Basic</option><option value="not_tested">Not tested</option>
                  </select>
                </Field>
                <label className="flex items-end gap-2 text-xs text-carriera-sec pb-5">
                  <input type="checkbox" checked={form.mandarin_confirmed} onChange={e => set('mandarin_confirmed', e.target.checked)} />
                  Mandarin confirmed on the call
                </label>
              </div>

              <Heading>Pay and start date</Heading>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Exp: expected salary (RM)" hint="Full number, e.g. 4500">
                  <input className={INPUT} type="number" min="0" step="0.01" value={form.expected_salary} onChange={e => set('expected_salary', e.target.value)} />
                </Field>
                <Field label="$ basic now (RM)">
                  <input className={INPUT} type="number" min="0" step="0.01" value={form.current_basic} onChange={e => set('current_basic', e.target.value)} />
                </Field>
                <Field label="Avail: can start" className="col-span-2">
                  <select className={INPUT} value={form.availability} onChange={e => set('availability', e.target.value)}>
                    <option value="">Not asked</option><option value="immediate">Immediate</option>
                    <option value="notice">After a notice period</option><option value="date">From a month and year</option>
                  </select>
                </Field>
                {form.availability === 'notice' && (
                  <Field label="Notice (months)">
                    <input className={INPUT} type="number" min="0" step="0.5" value={form.notice_months} onChange={e => set('notice_months', e.target.value)} />
                  </Field>
                )}
                {form.availability === 'date' && (
                  <Field label="Available from">
                    <input className={INPUT} value={form.available_from} onChange={e => set('available_from', e.target.value)} placeholder="e.g. July 2026" />
                  </Field>
                )}
              </div>
              <p className="text-xs text-carriera-muted mt-3 mb-1">Allowances now (transport, phone, ...)</p>
              {form.allowances.map((a, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <input className={INPUT} value={a.label} placeholder="e.g. Transport"
                    onChange={e => set('allowances', form.allowances.map((x, n) => n === i ? { ...x, label: e.target.value } : x))} />
                  <input className={`${INPUT} w-28`} type="number" min="0" step="0.01" value={a.amount} placeholder="RM"
                    onChange={e => set('allowances', form.allowances.map((x, n) => n === i ? { ...x, amount: e.target.value } : x))} />
                  <button type="button" onClick={() => set('allowances', form.allowances.filter((_, n) => n !== i))}
                    className="text-xs text-carriera-muted hover:text-red-500 px-1">x</button>
                </div>
              ))}
              <button type="button" onClick={() => set('allowances', [...form.allowances, { label: '', amount: '' }])}
                className="text-xs text-carriera-accent hover:underline">+ Add allowance</button>

              <Heading>Jobs (current first)</Heading>
              {form.jobs.map((j, i) => (
                <JobNotes key={i} job={j} index={i} onChange={job => setJob(i, job)}
                  onRemove={() => set('jobs', form.jobs.filter((_, n) => n !== i))} />
              ))}
              <button type="button" onClick={() => set('jobs', [...form.jobs, { ...BLANK_JOB }])}
                className="text-xs text-carriera-accent hover:underline">+ Add job</button>

              <Heading>Other notes</Heading>
              <textarea className={INPUT} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder="Anything else from the call" />
            </fieldset>

            {canEdit && (
              <div className="mt-5 space-y-3">
                <button onClick={onSave} disabled={busy}
                  className="w-full text-sm border border-carriera-text text-carriera-text px-4 py-2 hover:bg-carriera-bg disabled:opacity-40">
                  Save call notes
                </button>
                <div className="border-t border-carriera-border pt-3">
                  <Field label="Profile for which job">
                    <select className={INPUT} value={pick} onChange={e => setPick(e.target.value)}>
                      <option value="">Pick the job</option>
                      {screenings.map(s => <option key={s.id} value={s.id}>{jobLabel(s.jobs)}</option>)}
                    </select>
                  </Field>
                  <button onClick={onReady} disabled={busy || !pick || asked === pick}
                    className="w-full mt-2 text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
                    Ready for profile
                  </button>
                </div>
              </div>
            )}
            {message && <p className="text-xs text-carriera-sec mt-3">{message}</p>}

            {screenings.some(s => s.profile_path) && (
              <>
                <Heading>Profile drafts from Kate</Heading>
                {screenings.filter(s => s.profile_path).map(s => (
                  <button key={s.id} onClick={() => openProfile(s.id)} className="block text-xs text-carriera-accent hover:underline mb-1">
                    {jobLabel(s.jobs)}: download draft ({new Date(s.profile_ready_at).toLocaleDateString('en-MY')})
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
